import { create } from 'zustand'
import type { DriftBottle, Letter, SessionResponse } from '../types'

const STORAGE_KEY = 'dear-you-session-v2'
const MY_LETTERS_KEY = 'dear-you-my-letters'
const API_BASE = import.meta.env.VITE_API_BASE || '/api'

function getSessionId(): string {
  let sid = localStorage.getItem(STORAGE_KEY)
  if (!sid) {
    sid = crypto.randomUUID()
    localStorage.setItem(STORAGE_KEY, sid)
  }
  return sid
}

function getMyLetterIds(): string[] {
  try {
    const raw = localStorage.getItem(MY_LETTERS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function saveMyLetterIds(ids: string[]) {
  try {
    localStorage.setItem(MY_LETTERS_KEY, JSON.stringify(ids))
  } catch {
    // Storage may be unavailable (private mode); ownership is best-effort.
  }
}

function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const sessionId = getSessionId()
  headers['x-session-id'] = sessionId
  return fetch(`${API_BASE}${path}`, { ...init, headers: { ...init?.headers, ...headers } }).then(async r => {
    const data = await r.json().catch(() => ({}))
    if (!r.ok) {
      const message = (data as { error?: string }).error || `HTTP ${r.status}`
      throw new Error(message)
    }
    return data as T
  })
}

interface StoreState {
  letters: Letter[]
  leaderboard: Letter[]
  myLetterIds: string[]
  loading: boolean
  error: string | null
  session: SessionResponse | null
  language: 'zh-CN' | 'en'
  script: 'traditional' | 'simplified'
  showPinyin: boolean
  splashSeen: boolean
  nextCursor: string | null
  hasMore: boolean

  // Actions
  fetchLetters: (cursor?: string) => Promise<void>
  fetchLeaderboard: () => Promise<void>
  fetchLetter: (letterId: string) => Promise<Letter | null>
  submitLetter: (sender: string, recipient: string, message: string, rewritten?: { vernacular_text: string; qiaopi_text: string }) => Promise<{ letter: Letter; error?: string }>
  voteStamp: (letterId: string) => Promise<boolean>
  voteUnstamp: (letterId: string) => Promise<boolean>
  checkVote: (letterId: string) => Promise<boolean>
  fetchMyVotes: () => Promise<string[]>
  getSession: () => Promise<SessionResponse>
  throwDrift: (letterId: string) => Promise<boolean>
  receiveDrift: () => Promise<{ bottle_id: string; letter: Letter } | null>
  replyDrift: (bottleId: string, reply: string) => Promise<boolean>
  fetchMyDriftBottles: () => Promise<DriftBottle[]>
  setLanguage: (lang: 'zh-CN' | 'en') => void
  setScript: (script: 'traditional' | 'simplified') => void
  togglePinyin: () => void
  setSplashSeen: () => void
  softDeleteLetter: (letterId: string) => Promise<boolean>
}

export const useStore = create<StoreState>((set, get) => ({
  letters: [],
  leaderboard: [],
  myLetterIds: getMyLetterIds(),
  loading: false,
  error: null,
  session: null,
  language: 'zh-CN',
  script: 'simplified',
  showPinyin: false,
  splashSeen: false,
  nextCursor: null,
  hasMore: true,

  fetchLetters: async (cursor?: string) => {
    set({ loading: true, error: null })
    try {
      const params = new URLSearchParams()
      if (cursor) params.set('cursor', cursor)
      params.set('limit', '8')
      const data = await apiFetch<{ letters: Letter[]; next_cursor: string | null; has_more: boolean }>(`/letters?${params.toString()}`)
      set(state => ({
        letters: cursor ? [...state.letters, ...data.letters] : data.letters,
        nextCursor: data.next_cursor,
        hasMore: data.has_more,
        loading: false,
      }))
    } catch (e) {
      set({ error: (e as Error).message, loading: false, hasMore: false })
    }
  },

  fetchLeaderboard: async () => {
    try {
      const data = await apiFetch<{ letters: Letter[] }>('/letters?top20=true')
      set({ leaderboard: data.letters || [] })
    } catch {
      // Ignore leaderboard errors
    }
  },

  fetchLetter: async (letterId) => {
    try {
      const data = await apiFetch<{ letter: Letter }>(`/letters/${encodeURIComponent(letterId)}`)
      return data.letter
    } catch {
      return null
    }
  },

  submitLetter: async (sender, recipient, message, rewritten) => {
    set({ loading: true, error: null })
    try {
      const session = await get().getSession()
      if (session.rate_limited) {
        set({ loading: false })
        return { letter: null as any, error: 'Rate limit exceeded (3 letters per session)' }
      }

      const data = await apiFetch<{ letter: Letter }>(`/letters`, {
        method: 'POST',
        body: JSON.stringify({
          sender_name: sender,
          recipient_name: recipient,
          message,
          vernacular_text: rewritten?.vernacular_text,
          qiaopi_text: rewritten?.qiaopi_text,
        }),
      })
      const myLetterIds = [data.letter.id, ...get().myLetterIds.filter(id => id !== data.letter.id)].slice(0, 200)
      saveMyLetterIds(myLetterIds)
      set(state => ({
        letters: [data.letter, ...state.letters],
        myLetterIds,
        loading: false,
      }))
      return { letter: data.letter }
    } catch (e) {
      const err = (e as Error).message
      set({ error: err, loading: false })
      return { letter: null as any, error: err }
    }
  },

  voteStamp: async (letterId) => {
    const sessionId = getSessionId()
    try {
      await apiFetch('/votes/stamp', { method: 'POST', body: JSON.stringify({ letter_id: letterId, session_id: sessionId }) })
      set(state => ({
        letters: state.letters.map(l => l.id === letterId ? { ...l, stamp_count: l.stamp_count + 1 } : l),
        leaderboard: state.leaderboard.map(l => l.id === letterId ? { ...l, stamp_count: l.stamp_count + 1 } : l),
      }))
      get().fetchLeaderboard()
      return true
    } catch {
      return false
    }
  },

  voteUnstamp: async (letterId) => {
    const sessionId = getSessionId()
    try {
      await apiFetch('/votes/unstamp', { method: 'POST', body: JSON.stringify({ letter_id: letterId, session_id: sessionId }) })
      set(state => ({
        letters: state.letters.map(l => l.id === letterId ? { ...l, stamp_count: Math.max(0, l.stamp_count - 1) } : l),
        leaderboard: state.leaderboard.map(l => l.id === letterId ? { ...l, stamp_count: Math.max(0, l.stamp_count - 1) } : l),
      }))
      get().fetchLeaderboard()
      return true
    } catch {
      return false
    }
  },

  checkVote: async (letterId) => {
    const sessionId = getSessionId()
    try {
      const data = await apiFetch<{ voted: boolean }>(`/votes/check?letter_id=${letterId}&session_id=${sessionId}`)
      return data.voted
    } catch {
      return false
    }
  },

  fetchMyVotes: async () => {
    const sessionId = getSessionId()
    try {
      const data = await apiFetch<{ voted_ids: string[] }>(`/votes/mine?session_id=${sessionId}`)
      return data.voted_ids || []
    } catch {
      return []
    }
  },

  getSession: async () => {
    const data = await apiFetch<SessionResponse>('/session')
    set({ session: data })
    return data
  },

  throwDrift: async (letterId) => {
    const sessionId = getSessionId()
    try {
      await apiFetch('/drift/throw', { method: 'POST', body: JSON.stringify({ letter_id: letterId, session_id: sessionId }) })
      return true
    } catch {
      return false
    }
  },

  receiveDrift: async () => {
    const sessionId = getSessionId()
    try {
      const data = await apiFetch<{ bottle: { bottle_id: string; letter: Letter } | null }>(`/drift/receive?session_id=${sessionId}`)
      return data.bottle || null
    } catch {
      return null
    }
  },

  replyDrift: async (bottleId, reply) => {
    const sessionId = getSessionId()
    try {
      await apiFetch('/drift/reply', { method: 'POST', body: JSON.stringify({ bottle_id: bottleId, reply, session_id: sessionId }) })
      return true
    } catch {
      return false
    }
  },

  fetchMyDriftBottles: async () => {
    const sessionId = getSessionId()
    try {
      const data = await apiFetch<{ bottles: DriftBottle[] }>(`/drift/mine?session_id=${sessionId}`)
      return data.bottles || []
    } catch {
      return []
    }
  },

  setLanguage: (lang) => set({ language: lang }),
  setScript: (script) => set({ script }),
  togglePinyin: () => set(state => ({ showPinyin: !state.showPinyin })),
  setSplashSeen: () => {
    set({ splashSeen: true })
    localStorage.setItem('dear-you-splash-seen', 'true')
  },

  softDeleteLetter: async (letterId) => {
    try {
      await apiFetch(`/letters/${letterId}`, { method: 'PATCH', body: JSON.stringify({ deleted: true }) })
      const myLetterIds = get().myLetterIds.filter(id => id !== letterId)
      saveMyLetterIds(myLetterIds)
      set(state => ({
        myLetterIds,
        letters: state.letters.filter(l => l.id !== letterId),
        leaderboard: state.leaderboard.filter(l => l.id !== letterId),
      }))
      return true
    } catch (e) {
      set({ error: (e as Error).message })
      return false
    }
  },
}))
